// The River's spectrum display, inside the period panel: segmented bars like a display, in the
// panel's own colours, driven by the engine's real output (an AnalyserNode on the worklet). Unlit
// segments glow faintly, so the panel keeps its shape while nothing plays.
import { useEffect, useRef } from "react";
import { currentHost } from "../audio/host";
import { prefersCalm } from "../ui/settings";
import { bandLevels, makeBands, newMeter, stepMeter, type Bands, type Meter } from "./spectrum";

const SEG = 3; // segment height (CSS px)
const GAP = 1.5; // gap between segments
const BAR_GAP = 3; // gap between bars

export function Visualizer({ playing }: { playing: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const playingRef = useRef(playing);
  useEffect(() => {
    playingRef.current = playing;
  }, [playing]);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const g = canvas.getContext("2d");
    if (!g) return;
    let raf = 0;
    let last = performance.now();
    let lastReport = 0;
    let lastDraw = 0;
    let n = 0;
    let bands: Bands | null = null;
    let bandsFor = "";
    let meter: Meter = newMeter(0);
    let target = new Float32Array(0);
    let db: Float32Array<ArrayBuffer> = new Float32Array(0);
    let fg = "#7ff";
    let glow = "rgba(127,255,255,0.4)";
    let stillAt = ""; // the size at which the idle (all dark) picture was last drawn

    const readColours = () => {
      const cs = getComputedStyle(canvas);
      fg = cs.getPropertyValue("--oled-fg").trim() || fg;
      glow = cs.getPropertyValue("--oled-glow").trim() || glow;
    };
    readColours();

    const size = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
      }
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.max(20, Math.min(64, Math.round(w / 15)));
      if (count !== n) {
        n = count;
        meter = newMeter(n);
        target = new Float32Array(n);
        bandsFor = "";
      }
      return { w, h };
    };

    const draw = (w: number, h: number) => {
      g.clearRect(0, 0, w, h);
      const segs = Math.max(4, Math.floor((h + GAP) / (SEG + GAP)));
      const bw = (w - BAR_GAP * (n - 1)) / n;
      // Unlit segments: a faint grid, like an LCD.
      g.globalAlpha = 0.09;
      g.fillStyle = fg;
      g.beginPath();
      for (let i = 0; i < n; i++)
        for (let s = 0; s < segs; s++) g.rect(i * (bw + BAR_GAP), h - (s + 1) * SEG - s * GAP, bw, SEG);
      g.fill();
      // Lit segments, brighter towards the top, in one glowing pass.
      g.shadowColor = glow;
      g.shadowBlur = 8;
      for (const [from, to, alpha] of [
        [0, 0.5, 0.72],
        [0.5, 0.8, 0.88],
        [0.8, 1.01, 1],
      ] as const) {
        g.globalAlpha = alpha;
        g.beginPath();
        for (let i = 0; i < n; i++) {
          const lit = Math.round(meter.level[i] * segs);
          const s0 = Math.floor(from * segs);
          const s1 = Math.min(lit, Math.floor(to * segs));
          for (let s = s0; s < s1; s++) g.rect(i * (bw + BAR_GAP), h - (s + 1) * SEG - s * GAP, bw, SEG);
        }
        g.fill();
      }
      // Peak caps.
      g.globalAlpha = 0.95;
      g.beginPath();
      for (let i = 0; i < n; i++) {
        const s = Math.min(segs - 1, Math.round(meter.peak[i] * segs));
        if (meter.peak[i] > 0.02) g.rect(i * (bw + BAR_GAP), h - (s + 1) * SEG - s * GAP, bw, SEG * 0.7);
      }
      g.fill();
      g.shadowBlur = 0;
      g.globalAlpha = 1;
    };

    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      // Calm motion: about 12 frames a second.
      if (prefersCalm() && now - lastDraw < 80) return;
      lastDraw = now;
      const { w, h } = size();
      const host = currentHost();
      const a = host?.analyser;
      const playing = playingRef.current;
      if (playing && a && host.ctx.state === "running") {
        const key = `${n}:${a.context.sampleRate}:${a.fftSize}`;
        if (key !== bandsFor) {
          bands = makeBands(n, a.context.sampleRate, a.fftSize);
          bandsFor = key;
          db = new Float32Array(a.frequencyBinCount);
        }
        a.getFloatFrequencyData(db);
        bandLevels(db, bands!, target);
      } else {
        target.fill(0);
      }
      stepMeter(meter, target, dt);
      if (now - lastReport > 250) {
        lastReport = now;
        readColours();
        let sum = 0;
        for (let i = 0; i < n; i++) sum += meter.level[i];
        canvas.dataset.level = (n ? sum / n : 0).toFixed(3);
      }
      // Idle and dark: draw once, then rest until something changes.
      const dark = !playing && meter.peak.every((v) => v === 0);
      const sizeKey = `${w}x${h}:${fg}`;
      if (dark && stillAt === sizeKey) return;
      stillAt = dark ? sizeKey : "";
      draw(w, h);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);

  return <canvas ref={ref} className="today-vis" data-testid="visualizer" data-level="0" aria-hidden="true" />;
}
